import React, { Component, type ReactNode } from 'react';
import { Card, CardContent } from './card';
import { Button } from './Button';
import { AlertTriangle, RefreshCw, Copy, ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface ErrorDetails {
  message: string;
  stack?: string;
  chartDataSource?: 'JSON' | 'HTML' | 'unknown';
  timestamp: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorDetails: ErrorDetails | null;
  showDetails: boolean;
}

class ChartErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorDetails: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Determine chart data source from error message
    let chartDataSource: 'JSON' | 'HTML' | 'unknown' = 'unknown';
    const errorMessage = error.message.toLowerCase();
    if (errorMessage.includes('json') || errorMessage.includes('chart_data')) {
      chartDataSource = 'JSON';
    } else if (errorMessage.includes('html') || errorMessage.includes('parsing')) {
      chartDataSource = 'HTML';
    }

    const errorDetails: ErrorDetails = {
      message: error.message,
      stack: error.stack,
      chartDataSource,
      timestamp: new Date().toISOString()
    };

    console.error('Chart Error Boundary caught error:', { 
      error, 
      errorInfo, 
      chartDataSource,
      timestamp: errorDetails.timestamp
    });

    this.setState({ errorDetails });
  }

  componentDidUpdate(prevProps: Props): void {
    // Reset error state when children change (e.g., symbol/timeframe changes)
    if (prevProps.children !== this.props.children) {
      if (this.state.hasError) {
        this.setState({ 
          hasError: false, 
          error: null, 
          errorDetails: null,
          showDetails: false
        });
      }
    }
  }

  handleReset = (): void => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorDetails: null,
      showDetails: false
    });
    this.props.onReset?.();
  };

  handleCopyError = (): void => {
    if (!this.state.errorDetails) return;
    
    const errorText = `Chart Error Report
${'='.repeat(50)}
Timestamp: ${this.state.errorDetails.timestamp}
Chart Data Source: ${this.state.errorDetails.chartDataSource}
Error Message: ${this.state.errorDetails.message}

Stack Trace:
${this.state.errorDetails.stack || 'No stack trace available'}
${'='.repeat(50)}`;
    
    navigator.clipboard.writeText(errorText).then(() => {
      console.log('Error details copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy error details:', err);
    });
  };

  toggleDetails = (): void => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  getRecoverySuggestion = (): string => {
    const source = this.state.errorDetails?.chartDataSource;
    
    if (source === 'JSON') {
      return 'Try refreshing the forecast. The chart data may be corrupted.';
    } else if (source === 'HTML') {
      return 'Chart data parsing failed. Try refreshing or contact support.';
    } else {
      return 'Check your network connection and try refreshing.';
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI with enhanced details
      const { errorDetails, showDetails } = this.state;
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      return (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
              <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Chart Error</h3>
              <p className="text-sm text-muted-foreground mb-2">
                {this.state.error?.message || 'Failed to render chart'}
              </p>
              
              {errorDetails && (
                <>
                  <div className="text-xs text-muted-foreground mb-2">
                    <span className="font-medium">Chart Data Source:</span> {errorDetails.chartDataSource}
                    {' • '}
                    <span className="font-medium">Time:</span> {new Date(errorDetails.timestamp).toLocaleTimeString()}
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4 max-w-md">
                    {this.getRecoverySuggestion()}
                  </p>
                </>
              )}
              
              <div className="flex gap-2 mb-4">
                <Button onClick={this.handleReset}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
                
                {errorDetails && (
                  <Button variant="outline" onClick={this.handleCopyError}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Error Details
                  </Button>
                )}
              </div>
              
              {isDevelopment && errorDetails && (
                <div className="w-full max-w-2xl mt-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={this.toggleDetails}
                    className="mb-2"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-2" />
                        Hide Stack Trace
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Show Stack Trace
                      </>
                    )}
                  </Button>
                  
                  {showDetails && errorDetails.stack && (
                    <pre className="text-left text-xs bg-muted p-4 rounded-md overflow-auto max-h-60">
                      {errorDetails.stack}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export { ChartErrorBoundary };
